export type PixelPoint = {
  x: number;
  y: number;
};

export type PixelSize = {
  width: number;
  height: number;
};

type PopupAnchor = 'bottom' | 'top' | 'left' | 'right';

export type PopupPlacement = {
  anchor: PopupAnchor;
  offset: [number, number];
};

type Rect = {
  bottom: number;
  left: number;
  right: number;
  top: number;
};

type PopupPlacementOptions = {
  /** Inset to preserve between the popup and map boundary, in CSS pixels. */
  edgePadding: number;
  /** Current map-container dimensions in CSS pixels. */
  mapSize: PixelSize;
  /** Desired space between the marker pill and popup, in CSS pixels. */
  markerGap: number;
  /** Marker center relative to the map's top-left corner, in CSS pixels. */
  markerPoint: PixelPoint;
  /** Maximum dimensions of the centered marker pill, in CSS pixels. */
  markerSize: PixelSize;
  /** Measured popup dimensions in CSS pixels. */
  popupSize: PixelSize;
};

type NaturalRectOptions = {
  /** Popup edge Mapbox attaches to the marker coordinate. */
  anchor: PopupAnchor;
  /** Desired space between the marker pill and popup, in CSS pixels. */
  markerGap: number;
  /** Marker center relative to the map's top-left corner, in CSS pixels. */
  markerPoint: PixelPoint;
  /** Maximum dimensions of the centered marker pill, in CSS pixels. */
  markerSize: PixelSize;
  /** Measured popup dimensions in CSS pixels. */
  popupSize: PixelSize;
};

type ClampOptions = {
  /** Inclusive upper bound. */
  maximum: number;
  /** Inclusive lower bound. */
  minimum: number;
  /** Number to constrain to the inclusive bounds. */
  value: number;
};

type ShiftRectOptions = {
  /** Inset to preserve between the popup and map boundary, in CSS pixels. */
  edgePadding: number;
  /** Current map-container dimensions in CSS pixels. */
  mapSize: PixelSize;
  /** Candidate popup bounds relative to the map, in CSS pixels. */
  rect: Rect;
};

type OffsetOptions = {
  /** Popup edge Mapbox attaches to the marker coordinate. */
  anchor: PopupAnchor;
  /** Collision-corrected popup bounds relative to the map, in CSS pixels. */
  finalRect: Rect;
  /** Marker center relative to the map's top-left corner, in CSS pixels. */
  markerPoint: PixelPoint;
  /** Measured popup dimensions in CSS pixels. */
  popupSize: PixelSize;
};

type RectFromPositionOptions = {
  /** Horizontal position relative to the map's left edge, in CSS pixels. */
  left: number;
  /** Rectangle dimensions in CSS pixels. */
  size: PixelSize;
  /** Vertical position relative to the map's top edge, in CSS pixels. */
  top: number;
};

type RectPairOptions = {
  /** First rectangle in the comparison. */
  first: Rect;
  /** Second rectangle in the comparison. */
  second: Rect;
};

type BasePositionOptions = {
  /** Popup edge Mapbox attaches to the marker coordinate. */
  anchor: PopupAnchor;
  /** Marker center relative to the map's top-left corner, in CSS pixels. */
  markerPoint: PixelPoint;
  /** Measured popup dimensions in CSS pixels. */
  popupSize: PixelSize;
};

const ANCHOR_PREFERENCE: readonly PopupAnchor[] = ['bottom', 'top', 'left', 'right'];

function clamp({ maximum, minimum, value }: ClampOptions) {
  return Math.min(Math.max(value, minimum), maximum);
}

function rectFromPosition({ left, size, top }: RectFromPositionOptions): Rect {
  return {
    bottom: top + size.height,
    left,
    right: left + size.width,
    top,
  };
}

function naturalRect({
  anchor,
  markerGap,
  markerPoint,
  markerSize,
  popupSize,
}: NaturalRectOptions): Rect {
  const horizontalClearance = markerSize.width / 2 + markerGap;
  const verticalClearance = markerSize.height / 2 + markerGap;
  const base = basePosition({ anchor, markerPoint, popupSize });

  switch (anchor) {
    case 'top':
      return rectFromPosition({
        left: base.x,
        size: popupSize,
        top: base.y + verticalClearance,
      });
    case 'bottom':
      return rectFromPosition({
        left: base.x,
        size: popupSize,
        top: base.y - verticalClearance,
      });
    case 'left':
      return rectFromPosition({
        left: base.x + horizontalClearance,
        size: popupSize,
        top: base.y,
      });
    case 'right':
      return rectFromPosition({
        left: base.x - horizontalClearance,
        size: popupSize,
        top: base.y,
      });
  }
  throw new Error('Unsupported popup anchor');
}

function shiftInsideMap({ edgePadding, mapSize, rect }: ShiftRectOptions): Rect {
  const width = rect.right - rect.left;
  const height = rect.bottom - rect.top;
  const maximumLeft = mapSize.width - edgePadding - width;
  const maximumTop = mapSize.height - edgePadding - height;
  const left =
    maximumLeft >= edgePadding
      ? clamp({ maximum: maximumLeft, minimum: edgePadding, value: rect.left })
      : (mapSize.width - width) / 2;
  const top =
    maximumTop >= edgePadding
      ? clamp({ maximum: maximumTop, minimum: edgePadding, value: rect.top })
      : (mapSize.height - height) / 2;

  return rectFromPosition({ left, size: { height, width }, top });
}

function overlapArea({ first, second }: RectPairOptions) {
  const width = Math.max(
    0,
    Math.min(first.right, second.right) - Math.max(first.left, second.left),
  );
  const height = Math.max(
    0,
    Math.min(first.bottom, second.bottom) - Math.max(first.top, second.top),
  );
  return width * height;
}

function shiftDistance({ first, second }: RectPairOptions) {
  return Math.abs(first.left - second.left) + Math.abs(first.top - second.top);
}

function basePosition({ anchor, markerPoint, popupSize }: BasePositionOptions): PixelPoint {
  switch (anchor) {
    case 'top':
      return { x: markerPoint.x - popupSize.width / 2, y: markerPoint.y };
    case 'bottom':
      return {
        x: markerPoint.x - popupSize.width / 2,
        y: markerPoint.y - popupSize.height,
      };
    case 'left':
      return { x: markerPoint.x, y: markerPoint.y - popupSize.height / 2 };
    case 'right':
      return {
        x: markerPoint.x - popupSize.width,
        y: markerPoint.y - popupSize.height / 2,
      };
  }
  throw new Error('Unsupported popup anchor');
}

function popupOffset({
  anchor,
  finalRect,
  markerPoint,
  popupSize,
}: OffsetOptions): [number, number] {
  const base = basePosition({ anchor, markerPoint, popupSize });

  return [finalRect.left - base.x, finalRect.top - base.y];
}

export function getPopupPlacement({
  edgePadding,
  mapSize,
  markerGap,
  markerPoint,
  markerSize,
  popupSize,
}: PopupPlacementOptions): PopupPlacement {
  const markerRect = rectFromPosition({
    left: markerPoint.x - markerSize.width / 2,
    size: markerSize,
    top: markerPoint.y - markerSize.height / 2,
  });
  let best:
    | {
        anchor: PopupAnchor;
        finalRect: Rect;
        markerOverlap: number;
        shift: number;
      }
    | undefined;

  for (const anchor of ANCHOR_PREFERENCE) {
    const natural = naturalRect({ anchor, markerGap, markerPoint, markerSize, popupSize });
    const finalRect = shiftInsideMap({ edgePadding, mapSize, rect: natural });
    const candidate = {
      anchor,
      finalRect,
      markerOverlap: overlapArea({ first: finalRect, second: markerRect }),
      shift: shiftDistance({ first: natural, second: finalRect }),
    };
    if (
      !best ||
      candidate.markerOverlap < best.markerOverlap ||
      (candidate.markerOverlap === best.markerOverlap && candidate.shift < best.shift)
    ) {
      best = candidate;
    }
  }

  if (!best) {
    throw new Error('Popup placement requires at least one candidate anchor');
  }

  return {
    anchor: best.anchor,
    offset: popupOffset({
      anchor: best.anchor,
      finalRect: best.finalRect,
      markerPoint,
      popupSize,
    }),
  };
}
